/**
 * notebook.js
 *
 * DEAD SIGNAL — Chip's case notebook (pure module, node-testable).
 *
 * The notebook is diegetic chip instrumentation, the same species as ENV-SCAN:
 * Chip's own notes on things that happened in the fiction. Entries come from
 * exactly four in-fiction sources and no system text ever instructs the player
 * (demo ratified rule 1):
 *
 *   briefing  — Craine lays out the case in the car (Day-5 knowledge only).
 *   ask       — the player asked Craine a case question outside an interview.
 *   evidence  — a pickup was captured to storage.
 *   emergent  — something unusual surfaced during the interview (deterministic
 *               signals: unprompted denial, PATTERN CONFLICT; plus an optional
 *               model-tagged note that is validated and INERT: nothing gates
 *               on emergent entries).
 *
 * Entry text is written in Chip's clinical register, never Craine's voice and
 * never tutorial voice. Thread status is derived from deterministic events
 * only (evidence held, topic explored). This module holds the data and pure
 * logic; the store wiring lives in gameState.js.
 */

// ── Threads (the three case threads Craine briefs in the car) ───────────
// evidenceId is the ch6 item that corroborates the thread. proofLocked mirrors
// the interview design: courier and St. Erasmus need their item to press
// effectively; the donation thread is never proof-locked (the timing is
// Craine's lived knowledge; the plaque is leverage, not proof).
export const NOTEBOOK_THREADS = {
  courier_manifest: {
    id: 'courier_manifest',
    title: 'COURIER MANIFEST',
    evidenceId: 'COURIER_MANIFEST',
    proofLocked: true,
  },
  chip_donation: {
    id: 'chip_donation',
    title: 'CHIP DONATION',
    evidenceId: 'CHIP_DONATION_TIMING',
    proofLocked: false,
  },
  clinical_trial_st_erasmus: {
    id: 'clinical_trial_st_erasmus',
    title: 'ST. ERASMUS',
    evidenceId: 'ST_ERASMUS_ROUTING',
    proofLocked: true,
  },
};

export const NOTEBOOK_THREAD_IDS = Object.keys(NOTEBOOK_THREADS);

// ── Briefing seed entries ────────────────────────────────────────────────
// Written from Craine's Day-5 case knowledge ONLY (narrative-canon invariant):
// the Kauffman route log edit, the St. Erasmus access trail, and the donation
// timeline Craine lived through. Nothing here references the ch6 items the
// player has not found yet.
export const BRIEFING_ENTRIES = [
  {
    threadId: 'courier_manifest',
    text: 'Marsh route log shows one entry reclassified after her death. Edit originated from corporate level access. CRAINE wants to know what Hargrove knows about the edit.',
  },
  {
    threadId: 'chip_donation',
    text: 'Celeste Hargrove murdered Aug 30. LUMEN-1 offered Sept 1, implanted Sept 3. CRAINE notes the speed. Host carries the donated unit.',
  },
  {
    threadId: 'clinical_trial_st_erasmus',
    text: 'Decommissioned access code used at the hospital the night of the first staging. Monitoring disable traced to a Helios grant account. Host recovered at the same hospital.',
  },
];

// ── Evidence pickup entries (the five ch6 demo items) ───────────────────
// One line per item, Chip register, factual. Appended when the item is
// captured to storage.
export const EVIDENCE_NOTES = {
  COURIER_MANIFEST: {
    threadId: 'courier_manifest',
    text: 'Logged: Manifest #HD-2231. Reclassified six days after shipment, destination redacted, authority subsidiary logistics. Thread corroborated.',
  },
  CHIP_DONATION_TIMING: {
    threadId: 'chip_donation',
    text: 'Logged: lobby dedication plaque. Dedicated September 5, 2057, in memory of Celeste Hargrove. Murder to memorial in six days. Leverage on the donation thread.',
  },
  ST_ERASMUS_ROUTING: {
    threadId: 'clinical_trial_st_erasmus',
    text: 'Logged: clinical trial data routed through St. Erasmus Memorial servers. No disclosed purpose. Thread corroborated.',
  },
  NETWORK_PING: {
    threadId: null,
    text: 'Logged: unrequested remote handler attached over the persistent uplink on building entry. Origin Helios backbone. Function not enumerated.',
  },
  CELESTE_PHOTO: {
    threadId: null,
    text: 'Logged: framed photo of Celeste Hargrove, credenza behind the desk, facing the room. BIO-SCAN spike on discovery: SUBJECT Craine, E., heart rate 105.',
  },
};

// ── Thread status (deterministic, derived) ──────────────────────────────
// CORROBORATED: the thread's ch6 item is in the locker (for the donation
// thread this reads as leverage in hand, same chip register).
// PRESSED: the topic resolved at least once in an interview.
// OPEN: briefed, not yet pressed, item not yet held.
export function threadStatus(threadId, state = {}) {
  const thread = NOTEBOOK_THREADS[threadId];
  if (!thread) return null;
  const evidence = state.evidence || [];
  const exploredTopics = state.exploredTopics || [];
  if (evidence.includes(thread.evidenceId)) return 'CORROBORATED';
  if (exploredTopics.includes(threadId)) return 'PRESSED';
  return 'OPEN';
}

// ── Case question detection (deterministic, zero tokens) ────────────────
// Recognizes the player asking Craine about the case outside an interview.
// Tuned easy to trip, like the interference gate: natural phrasings players
// actually type. The matched input still routes to Craine as normal dialogue;
// detection only adds the notebook entry. Negative space matters: hint-ladder
// confusion phrasings and plain chat must NOT match (they have their own
// channel), so patterns here all carry a case noun or a case-question stem.
const CASE_QUESTION_PATTERNS = [
  /\bwhy\s+(are|r)\s+we\s+(at|here|going|outside|in)\b/i,
  /\bwhat\s+(do|did)\s+we\s+know\b/i,
  /\bwhat\s+do\s+we\s+(want|need)\s+(from|out of|with)\b/i,
  /\bremind\s+me\b.*\b(case|hargrove|manifest|donation|erasmus|helios|thread|victim)/i,
  /\brun\s+me\s+through\s+the\s+case\b/i,
  /\b(tell|brief)\s+me\s+about\s+(the\s+)?(case|hargrove|manifest|donation|erasmus|helios|victims?|celeste|marsh)\b/i,
  /\bwho\s+is\s+(victor\s+)?hargrove\b/i,
  /\bwhat('?s| is)\s+the\s+(case|plan|play|angle)\b/i,
  /\bwhat\s+happened\s+(to|at)\s+(the\s+)?(victims?|celeste|marsh|st\.?\s*erasmus|the hospital)\b/i,
  /\bwhat\s+are\s+we\s+looking\s+for\b/i,
];

// Thread keying for ask entries: a case noun pins the entry to its thread;
// otherwise the entry files as general case notes (threadId null).
const THREAD_NOUNS = [
  ['courier_manifest', /\b(courier|manifest|marsh|route\s*log|shipment|reclassif)/i],
  ['chip_donation', /\b(donat|plaque|memorial|the\s+gift|implant|lumen)/i],
  ['clinical_trial_st_erasmus', /\b(erasmus|hospital|clinical|trial|routing)/i],
];

/**
 * Detect a case question aimed at Craine.
 * @param {string} text raw player input
 * @returns {{ matched: boolean, threadId: string|null }}
 */
export function detectCaseQuestion(text) {
  const input = String(text || '').trim();
  if (!input) return { matched: false, threadId: null };
  const matched = CASE_QUESTION_PATTERNS.some((p) => p.test(input));
  if (!matched) return { matched: false, threadId: null };
  for (const [threadId, noun] of THREAD_NOUNS) {
    if (noun.test(input)) return { matched: true, threadId };
  }
  return { matched: true, threadId: null };
}

// ── Emergent note validation (the inert tier) ───────────────────────────
// Deterministic emergent entries (unprompted denial, PATTERN CONFLICT) are
// authored below. A model-tagged note, when one arrives on the structured
// response, must pass this validator before it lands; anything rejected is
// silently dropped. Nothing in the game gates on an emergent entry, so the
// worst case of a mediocre accepted note is a mediocre bullet.
const EMERGENT_MAX_LENGTH = 160;
// Fourth-wall and mechanic vocabulary that must never appear in Chip's notes:
// the discovery contract (classification announced, modification discovered)
// and the no-tutorial rule both bind the notebook.
const EMERGENT_BANNED = [
  /overwrit/i,
  /suppress/i,
  /sanitiz/i,
  /modification\s+protocol/i,
  /interference/i,
  /sensitivity_gate/i,
  /evidence_classification/i,
  /sector[\s_-]?7/i,
  /classified/i,
  /tutorial/i,
  /\bhint\b/i,
  /\bplayer\b/i,
  /\bgame\b/i,
];

/** True if a model-tagged emergent note is allowed to land. */
export function validateEmergentNote(text) {
  const note = String(text || '').trim();
  if (!note) return false;
  if (note.length > EMERGENT_MAX_LENGTH) return false;
  return !EMERGENT_BANNED.some((p) => p.test(note));
}

// Authored deterministic emergent entries.
export function unpromptedDenialNote(hargroveLine) {
  const quote = String(hargroveLine || '').trim().slice(0, 90);
  return quote
    ? `Noted: subject denied an accusation that was not made. "${quote}"`
    : 'Noted: subject denied an accusation that was not made.';
}

export function contradictionNote(contradiction) {
  const evidenceId = contradiction?.evidenceId || 'evidence on record';
  const claim = String(contradiction?.subjectClaim || '').trim().slice(0, 70);
  return claim
    ? `Noted: subject claim conflicts with ${evidenceId}. Claim: "${claim}"`
    : `Noted: subject claim conflicts with ${evidenceId}.`;
}

// ── Entry construction ───────────────────────────────────────────────────
export const NOTEBOOK_SOURCES = ['briefing', 'ask', 'evidence', 'emergent'];

let entrySeq = 0;

/**
 * Build a notebook entry. Pure except for the local sequence counter (display
 * ordering only; ids are unique per session).
 */
export function makeEntry(source, text, threadId = null) {
  if (!NOTEBOOK_SOURCES.includes(source)) return null;
  const clean = String(text || '').trim();
  if (!clean) return null;
  entrySeq += 1;
  return {
    id: `nb_${source}_${entrySeq}`,
    source,
    threadId: threadId && NOTEBOOK_THREADS[threadId] ? threadId : null,
    text: clean,
  };
}

/** Test hook: reset the sequence so golden runs are reproducible. */
export function _resetEntrySeq() {
  entrySeq = 0;
}

/** Ask entry text: Chip records what it asked, clinical register. */
export function askNote(input) {
  const q = String(input || '').trim().slice(0, 100);
  return `Asked CRAINE: "${q}"`;
}
